import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { TemplateRef, Directive, ContentChild, EventEmitter, ContentChildren, Input, Output, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { getDataAttributes } from '@angular-react/fabric/lib/utils';
import { ChangeableItemDirective, ChangeableItemsHelper } from '@angular-react/fabric/lib/components/core';

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Wrapper directive to allow rendering a custom item to a ContextualMenuItem.
 */
class ContextualMenuItemRenderDirective {
}
/** @nocollapse */ ContextualMenuItemRenderDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ContextualMenuItemRenderDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ ContextualMenuItemRenderDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: ContextualMenuItemRenderDirective, selector: "fab-command-bar-item > render", queries: [{ propertyName: "templateRef", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ContextualMenuItemRenderDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-command-bar-item > render' }]
        }], propDecorators: { templateRef: [{
                type: ContentChild,
                args: [TemplateRef]
            }] } });
/**
 * Wrapper directive to allow rendering a custom icon to a ContextualMenuItem.
 */
class ContextualMenuItemRenderIconDirective {
}
/** @nocollapse */ ContextualMenuItemRenderIconDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ContextualMenuItemRenderIconDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ ContextualMenuItemRenderIconDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: ContextualMenuItemRenderIconDirective, selector: "fab-command-bar-item > render-icon", queries: [{ propertyName: "templateRef", first: true, predicate: TemplateRef, descendants: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ContextualMenuItemRenderIconDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-command-bar-item > render-icon' }]
        }], propDecorators: { templateRef: [{
                type: ContentChild,
                args: [TemplateRef]
            }] } });
class ContextualMenuItemDirective extends ChangeableItemDirective {
    constructor(elementRef) {
        super();
        this.elementRef = elementRef;
        this.click = new EventEmitter();
    }
    get onChildItemChanged() {
        return this._changeableItemsHelper && this._changeableItemsHelper.onChildItemChanged;
    }
    get onItemsChanged() {
        return this._changeableItemsHelper && this._changeableItemsHelper.onItemsChanged;
    }
    ngAfterContentInit() {
        if (this.renderDirective && this.renderDirective.templateRef) {
            this.render = this.renderDirective.templateRef;
        }
        if (this.renderIconDirective && this.renderIconDirective.templateRef) {
            this.renderIcon = this.renderIconDirective.templateRef;
        }
        this._changeableItemsHelper = new ChangeableItemsHelper(this.menuItemsDirectives, this, nonSelfDirective => {
            const items = nonSelfDirective.map(directive => this._directiveToContextualMenuItem(directive));
            if (!this.subMenuProps) {
                this.subMenuProps = { items: items };
            }
            else {
                this.subMenuProps.items = items;
            }
        });
    }
    ngOnDestroy() {
        this._changeableItemsHelper.destroy();
    }
    _directiveToContextualMenuItem(directive) {
        return Object.assign(Object.assign(Object.assign({}, directive), getDataAttributes(directive.elementRef.nativeElement, true)), { onClick: (ev, item) => {
                directive.click.emit({ ev: ev && ev.nativeEvent, item: item });
            } });
    }
}
/** @nocollapse */ ContextualMenuItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ContextualMenuItemDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ ContextualMenuItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: ContextualMenuItemDirective, selector: "contextual-menu-item", inputs: { componentRef: "componentRef", text: "text", secondaryText: "secondaryText", itemType: "itemType", iconProps: "iconProps", submenuIconProps: "submenuIconProps", disabled: "disabled", primaryDisabled: "primaryDisabled", shortCut: "shortCut", canCheck: "canCheck", checked: "checked", split: "split", data: "data", href: "href", target: "target", rel: "rel", subMenuProps: "subMenuProps", getItemClassNames: "getItemClassNames", itemProps: "itemProps", getSplitButtonVerticalDividerClassNames: "getSplitButtonVerticalDividerClassNames", sectionProps: "sectionProps", className: "className", style: "style", ariaLabel: "ariaLabel", title: "title", onMouseDown: "onMouseDown", role: "role", customOnRenderListLength: "customOnRenderListLength", keytipProps: "keytipProps", inactive: "inactive", name: "name", render: "render", renderIcon: "renderIcon" }, outputs: { click: "click", onChildItemChanged: "onChildItemChanged", onItemsChanged: "onItemsChanged" }, queries: [{ propertyName: "renderDirective", first: true, predicate: ContextualMenuItemRenderDirective, descendants: true }, { propertyName: "renderIconDirective", first: true, predicate: ContextualMenuItemRenderIconDirective, descendants: true }, { propertyName: "menuItemsDirectives", predicate: ContextualMenuItemDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: ContextualMenuItemDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'contextual-menu-item' }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { menuItemsDirectives: [{
                type: ContentChildren,
                args: [ContextualMenuItemDirective]
            }], renderDirective: [{
                type: ContentChild,
                args: [ContextualMenuItemRenderDirective]
            }], renderIconDirective: [{
                type: ContentChild,
                args: [ContextualMenuItemRenderIconDirective]
            }], componentRef: [{
                type: Input
            }], text: [{
                type: Input
            }], secondaryText: [{
                type: Input
            }], itemType: [{
                type: Input
            }], iconProps: [{
                type: Input
            }], submenuIconProps: [{
                type: Input
            }], disabled: [{
                type: Input
            }], primaryDisabled: [{
                type: Input
            }], shortCut: [{
                type: Input
            }], canCheck: [{
                type: Input
            }], checked: [{
                type: Input
            }], split: [{
                type: Input
            }], data: [{
                type: Input
            }], href: [{
                type: Input
            }], target: [{
                type: Input
            }], rel: [{
                type: Input
            }], subMenuProps: [{
                type: Input
            }], getItemClassNames: [{
                type: Input
            }], itemProps: [{
                type: Input
            }], getSplitButtonVerticalDividerClassNames: [{
                type: Input
            }], sectionProps: [{
                type: Input
            }], className: [{
                type: Input
            }], style: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], title: [{
                type: Input
            }], onMouseDown: [{
                type: Input
            }], role: [{
                type: Input
            }], customOnRenderListLength: [{
                type: Input
            }], keytipProps: [{
                type: Input
            }], inactive: [{
                type: Input
            }], name: [{
                type: Input
            }], render: [{
                type: Input
            }], renderIcon: [{
                type: Input
            }], click: [{
                type: Output
            }], onChildItemChanged: [{
                type: Output
            }], onItemsChanged: [{
                type: Output
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
const components = [
    ContextualMenuItemDirective,
    ContextualMenuItemRenderDirective,
    ContextualMenuItemRenderIconDirective,
];
class FabContextualMenuModule {
}
/** @nocollapse */ FabContextualMenuModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabContextualMenuModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ FabContextualMenuModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabContextualMenuModule, declarations: [ContextualMenuItemDirective,
        ContextualMenuItemRenderDirective,
        ContextualMenuItemRenderIconDirective], imports: [CommonModule], exports: [ContextualMenuItemDirective,
        ContextualMenuItemRenderDirective,
        ContextualMenuItemRenderIconDirective] });
/** @nocollapse */ FabContextualMenuModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabContextualMenuModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabContextualMenuModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: components,
                    exports: components,
                    schemas: [NO_ERRORS_SCHEMA],
                }]
        }] });

// Copyright (c) Microsoft Corporation. All rights reserved.

/**
 * Generated bundle index. Do not edit.
 */

export { ContextualMenuItemDirective, ContextualMenuItemRenderDirective, ContextualMenuItemRenderIconDirective, FabContextualMenuModule };
